import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Divider,
  Stack,
  RadioGroup,
  FormControlLabel,
  Radio,
  Switch,
  FormGroup,
  Paper,
  Tabs,
  Tab,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  InputAdornment,
  Chip,
  Autocomplete,
  useTheme,
  useMediaQuery,
  Drawer,
  IconButton,
  AppBar,
  Toolbar
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { getAllBusinessAdminSettings, getBusinessAdminSettings, updateBusinessAdminSettings } from '../../../utils/api';

const SECTIONS = [
  { label: 'Admin Profile', key: 'profile' },
  { label: 'Business WhatsApp Settings', key: 'global' },
  { label: 'WhatsApp Config', key: 'whatsapp' },
];

export default function Settings() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);

  // State for fields
  const [profile, setProfile] = useState({
    name: '',
    email: '',
    password: '',
  });

  const [whatsappEnabled, setWhatsappEnabled] = useState(false);
  const [tab, setTab] = useState(0);
  

  
  // Business-level settings state
  const [businessSettings, setBusinessSettings] = useState([]);
  const [selectedBusiness, setSelectedBusiness] = useState('');
  const [selectedBusinessSettings, setSelectedBusinessSettings] = useState({
    whatsapp_send_as: 'pdf',
    business_name: '',
    updated_at: ''
  });
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsError, setSettingsError] = useState('');
  const [settingsSuccess, setSettingsSuccess] = useState('');



  // Fetch business settings on component mount
  useEffect(() => {
    fetchBusinessSettings();
  }, []);

  const fetchBusinessSettings = async () => {
    try {
      setSettingsLoading(true);
      const response = await getAllBusinessAdminSettings();
      const data = response.data;
      setBusinessSettings(data.businesses || []);
      if (data.businesses && data.businesses.length > 0) {
        setSelectedBusiness(data.businesses[0].business_id);
      }
    } catch (err) {
      setSettingsError('Failed to fetch business settings');
    } finally {
      setSettingsLoading(false);
    }
  };

  // Fetch specific business settings when selection changes
  useEffect(() => {
    if (selectedBusiness) {
      fetchSpecificBusinessSettings(selectedBusiness);
    }
  }, [selectedBusiness]);

  const fetchSpecificBusinessSettings = async (businessId) => {
    try {
      setSettingsLoading(true);
      const response = await getBusinessAdminSettings(businessId);
      const data = response.data;
      setSelectedBusinessSettings({
        whatsapp_send_as: data.whatsapp_send_as || 'pdf',
        business_name: data.business_name || '',
        updated_at: data.updated_at || ''
      });
    } catch (err) {
      setSettingsError('Failed to fetch business settings');
    } finally {
      setSettingsLoading(false);
    }
  };

  const updateBusinessSettings = async () => {
    if (!selectedBusiness) return;
    try {
      setSettingsLoading(true);
      await updateBusinessAdminSettings(selectedBusiness, selectedBusinessSettings.whatsapp_send_as);
      setSettingsSuccess('Business WhatsApp mode updated successfully!');
      setSettingsError('');
      // Refresh settings to get updated timestamp
      await fetchSpecificBusinessSettings(selectedBusiness);
    } catch (err) {
      setSettingsError('Failed to update business WhatsApp mode');
      setSettingsSuccess('');
    } finally {
      setSettingsLoading(false);
    }
  };



  // Handlers
  const handleProfileChange = (e) => {
    setProfile({ ...profile, [e.target.name]: e.target.value });
  };

  const handleTabChange = (e, newValue) => {
    setTab(newValue);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const handleSave = () => {
    // Placeholder for save action
    alert('Changes saved (not really, just a demo)!');
  };

  // Section content renderers
  const renderSection = () => {
    switch (SECTIONS[tab].key) {
      case 'profile':
        return (
          <Paper elevation={0} sx={{ 
            p: { xs: 2, sm: 3, md: 4 }, 
            width: '100%', 
            maxWidth: { xs: '100%', sm: 600 },
            border: '1px solid',
            borderColor: 'divider'
          }}>
            <Typography variant="h6" fontWeight={600} gutterBottom color="text.primary">
              Admin Profile
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="body1" color="text.secondary" align="center">
              Coming Soon. Profile management will be handled in a dedicated section.
            </Typography>
          </Paper>
        );

      case 'global':
        return (
          <Paper elevation={0} sx={{ 
            p: { xs: 2, sm: 3, md: 4 }, 
            width: '100%', 
            maxWidth: { xs: '100%', sm: 600 },
            border: '1px solid',
            borderColor: 'divider'
          }}>
            <Typography variant="h6" fontWeight={600} gutterBottom color="primary.main">
              Business WhatsApp Settings
            </Typography>
            <Divider sx={{ mb: 2 }} />
            {settingsError && <Alert severity="error" sx={{ mb: 2 }}>{settingsError}</Alert>}
            {settingsSuccess && <Alert severity="success" sx={{ mb: 2 }}>{settingsSuccess}</Alert>}
            <Stack spacing={3}>
              {/* Business Selection */}
              <Box>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Select Business
                </Typography>
                
                <Autocomplete
                  options={businessSettings}
                  getOptionLabel={(option) => option.business_name}
                  value={businessSettings.find(b => b.business_id === selectedBusiness) || null}
                  onChange={(event, newValue) => {
                    setSelectedBusiness(newValue ? newValue.business_id : '');
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Search and select business"
                      placeholder="Type to search businesses..."
                      size="small"
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        width: '100%',
                        flexDirection: { xs: 'column', sm: 'row' },
                        gap: { xs: 1, sm: 0 }
                      }}>
                        <Typography variant="body2">{option.business_name}</Typography>
                        <Chip 
                          label={option.whatsapp_send_as} 
                          size="small" 
                          color={option.whatsapp_send_as === 'pdf' ? 'primary' : 'secondary'}
                          variant="outlined"
                        />
                      </Box>
                    </Box>
                  )}
                  sx={{ mb: 2 }}
                />
              </Box>

              {/* Settings History */}
              {selectedBusinessSettings.updated_at && (
                <Box sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: 'action.selected', borderRadius: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Business:</strong> {selectedBusinessSettings.business_name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Last Updated:</strong> {new Date(selectedBusinessSettings.updated_at).toLocaleString()}
                  </Typography>
                </Box>
              )}

              {/* Business WhatsApp Send Mode */}
              <Box>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  WhatsApp Send Mode
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  This setting controls how invoices are sent via WhatsApp for the selected business
                </Typography>
                <RadioGroup
                  value={selectedBusinessSettings.whatsapp_send_as}
                  onChange={(e) => setSelectedBusinessSettings({
                    ...selectedBusinessSettings,
                    whatsapp_send_as: e.target.value
                  })}
                >
                  <FormControlLabel 
                    value="pdf" 
                    control={<Radio />} 
                    label={
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="body2">Send as PDF</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Actual PDF file
                        </Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel 
                    value="link" 
                    control={<Radio />} 
                    label={
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="body2">Send as Link</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Link to invoice/receipt
                        </Typography>
                      </Box>
                    }
                  />
                </RadioGroup>
              </Box>

              {/* Business Impact Display */}
              <Box sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle2" fontWeight={600} color="text.primary" gutterBottom>
                  Business Impact
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  This setting affects only the selected business. 
                  Each business can have its own WhatsApp send mode setting.
                </Typography>
              </Box>

              {/* Save Button */}
              <Button 
                variant="contained" 
                color="primary" 
                onClick={updateBusinessSettings}
                disabled={settingsLoading || !selectedBusiness}
                sx={{ mt: 2 }}
                fullWidth={isMobile}
              >
                {settingsLoading ? 'Updating...' : 'Update Business WhatsApp Mode'}
              </Button>
            </Stack>
          </Paper>
        );

      case 'whatsapp':
        return (
          <Paper elevation={0} sx={{ 
            p: { xs: 2, sm: 3, md: 4 }, 
            width: '100%', 
            maxWidth: { xs: '100%', sm: 600 },
            border: '1px solid',
            borderColor: 'divider'
          }}>
            <Typography variant="h6" fontWeight={600} gutterBottom color="primary.main">
              WhatsApp Configuration
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <FormGroup>
              <FormControlLabel
                control={<Switch checked={true} disabled />}
                label="Enable WhatsApp Messaging for Invoices"
              />
            </FormGroup>
          </Paper>
        );
      default:
        return null;
    }
  };

  // Mobile navigation drawer
  const drawer = (
    <Box sx={{ width: 250 }}>
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="h6" fontWeight={700} color="primary">
          Settings
        </Typography>
      </Box>
      <Tabs
        orientation="vertical"
        value={tab}
        onChange={handleTabChange}
        aria-label="Settings Sections"
        sx={{ height: '100%' }}
      >
        {SECTIONS.map((section, idx) => (
          <Tab
            key={section.key}
            label={<Typography fontWeight={tab === idx ? 700 : 500}>{section.label}</Typography>}
            sx={{ alignItems: 'flex-start', px: 3, py: 2, textAlign: 'left' }}
          />
        ))}
      </Tabs>
    </Box>
  );

  return (
    <Box sx={{ 
      background: '#f7f9fa', 
      minHeight: '100vh',
      pb: { xs: 2, sm: 0 }
    }}>
      {/* Mobile App Bar */}
      {isMobile && (
        <AppBar position="sticky" color="default" elevation={1}>
          <Toolbar>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={() => setMobileOpen(!mobileOpen)}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" fontWeight={700}>
              Admin Settings
            </Typography>
          </Toolbar>
        </AppBar>
      )}

      <Box sx={{ 
        p: { xs: 1, sm: 2, md: 3 }, 
        maxWidth: 1200, 
        mx: 'auto'
      }}>
        {/* Desktop Header */}
        {!isMobile && (
          <Typography 
            variant="h4" 
            fontWeight={700} 
            gutterBottom 
            align="center" 
            sx={{ mb: 4 }}
          >
            Admin Settings
          </Typography>
        )}

        {/* Desktop Layout */}
        {!isMobile && (
          <Paper elevation={3} sx={{ display: 'flex', minHeight: 500 }}>
            {/* Vertical Tabs Navigation */}
            <Box sx={{ 
              borderRight: 1, 
              borderColor: 'divider', 
              minWidth: 220, 
              background: '#f0f4f8', 
              py: 3 
            }}>
              <Tabs
                orientation="vertical"
                value={tab}
                onChange={handleTabChange}
                aria-label="Settings Sections"
                variant="scrollable"
                sx={{ height: '100%' }}
              >
                {SECTIONS.map((section, idx) => (
                  <Tab
                    key={section.key}
                    label={<Typography fontWeight={tab === idx ? 700 : 500}>{section.label}</Typography>}
                    sx={{ alignItems: 'flex-start', px: 3, py: 2, textAlign: 'left' }}
                  />
                ))}
              </Tabs>
            </Box>
            {/* Section Content */}
            <Box sx={{ 
              flex: 1, 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center', 
              py: 5,
              px: { xs: 2, sm: 3, md: 4 }
            }}>
              {renderSection()}
            </Box>
          </Paper>
        )}

        {/* Mobile Layout */}
        {isMobile && (
          <>
            {/* Mobile Drawer */}
            <Drawer
              variant="temporary"
              open={mobileOpen}
              onClose={() => setMobileOpen(false)}
              ModalProps={{
                keepMounted: true, // Better open performance on mobile.
              }}
              sx={{
                display: { xs: 'block' },
                '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 250 },
              }}
            >
              {drawer}
            </Drawer>

            {/* Mobile Content */}
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'flex-start',
              pt: 2
            }}>
              {renderSection()}
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
} 
